import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useFocus } from "~/contexts/FocusContext";
import { AppCommand } from "~/types/commands";
import { ActionDefinition } from "~/types/actions";
import { useCharacterStore } from "~/stores/characterStore";
import { useNotifications } from "~/contexts/useNotifications";
import { useDialog } from "~/contexts/useDialog";
import { useRolling } from "~/hooks/useRolling";
import CompactCharacterCard from "~/pages/GmView/widgets/CompactCharacterCard/CompactCharacterCard";
import MinimizedCharacterCard from "~/pages/GmView/widgets/MinimizedCharacterCard/MinimizedCharacterCard";
import CharacterSearch from "~/widgets/CharacterSearch/CharacterSearch";
import TableSearch from "~/widgets/TableSearch/TableSearch";
import EmptyTableState from "~/widgets/EmptyTableState/EmptyTableState";
import RollSearch from "~/pages/CharacterSheet/widgets/RollSearch/RollSearch";
import SettingsPopup from "~/widgets/SettingsPopup/SettingsPopup";
import { Badge } from "~/widgets/Badge/Badge";
import { Button } from "~/widgets/Button/Button";
import { computeHpMax, computeResilienceMax, computeMovement, computeSkills } from "~/utils/characterDerived";
import {
  DAMAGE_DIALOG,
  HEAL_DIALOG,
  DIRECT_DAMAGE_DIALOG,
  SPEND_RESILIENCE_DIALOG,
  DELETE_CHARACTER_DIALOG,
  getTagActionDialog,
} from "~/rules/dialogMessages";
import { TagActionType } from "~/types/character";
import type { CharacterData } from "~/types/storage";
import type { Skill, TagAction } from "~/types/character";
import { generateUUID } from "~/utils/uuid";
import { getTableActions } from "~/pages/shared/tableActions";
import styles from "~/pages/GmView/GmView.scss";

/**
 * Get the stat type from a focused element by checking data attributes
 */
const getFocusedStatType = (): "hp" | "resilience" | "strain" | null => {
  const active = document.activeElement;
  if (!active) return null;

  // Check if active element or its ancestors have the data-stat-type attribute
  const statElement = active.closest("[data-stat-type]");
  if (statElement) {
    const statType = statElement.getAttribute("data-stat-type");
    if (statType === "hp" || statType === "resilience" || statType === "strain") {
      return statType;
    }
  }
  return null;
};

/**
 * CharacterSheetCompact Page (GM View) - Inner component
 *
 * Displays all characters in a responsive grid of compact cards.
 * Features:
 * - Import characters from JSON files
 * - Quick roll button per character
 * - Editable HP, Resilience, Strain
 * - Keyboard navigation between cards
 *
 * @hotkeys
 * - `C`: Open character search/switcher
 * - `←/→`: Navigate between cards
 * - `Tab/Shift-Tab`: Move between elements within card (native behavior)
 * - `↑/↓`: Adjust currently focused stat value
 * - `1-9`: Jump to card by position
 * - `R`: Open roll search
 * - `D/H/X/V`: Open damage/heal/direct/resilience dialogs
 * - `S`: Focus strain for adjustment
 */
const GmView: React.FC = () => {
  const allCharacters = useCharacterStore((state) => state.characters);
  const getActiveCharacter = useCharacterStore((state) => state.getActiveCharacter);
  const getCharactersForActiveTable = useCharacterStore((state) => state.getCharactersForActiveTable);
  const setActiveCharacter = useCharacterStore((state) => state.setActiveCharacter);
  const addCharacter = useCharacterStore((state) => state.addCharacter);
  const removeCharacter = useCharacterStore((state) => state.removeCharacter);
  const importCharacterToCollection = useCharacterStore((state) => state.importCharacterToCollection);
  const updateCharacter = useCharacterStore((state) => state.updateCharacter);

  // Access table management
  const tables = useCharacterStore((state) => state.tables);
  const activeTableId = useCharacterStore((state) => state.activeTableId);
  const getActiveTable = useCharacterStore((state) => state.getActiveTable);
  const setActiveTable = useCharacterStore((state) => state.setActiveTable);
  const addTable = useCharacterStore((state) => state.addTable);
  const removeTable = useCharacterStore((state) => state.removeTable);
  const renameTable = useCharacterStore((state) => state.renameTable);
  const assignCharacterToTable = useCharacterStore((state) => state.assignCharacterToTable);

  const activeCharacterId = getActiveCharacter()?.id || '';
  const tableCharacters = getCharactersForActiveTable();
  const activeTable = getActiveTable();

  // Use global notification and dialog contexts
  const { addTagNotification } = useNotifications();
  const { openDialog } = useDialog();
  const { rollDice } = useRolling();

  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const [isCharacterSearchOpen, setIsCharacterSearchOpen] = useState(false);
  const [isTableSearchOpen, setIsTableSearchOpen] = useState(false);
  const [isRollSearchOpen, setIsRollSearchOpen] = useState(false);
  const [rollSearchCharacterId, setRollSearchCharacterId] = useState<string | null>(null);
  const [isMinimizedExpanded, setIsMinimizedExpanded] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Define handler functions before handleCommand
  const createNewCharacter = useCallback(() => {
    const newChar = {
      id: crypto.randomUUID(),
      version: 4 as const,
      name: "New Adventurer",
      coreStats: { Mind: 2, Body: 3, Reflex: 2 },
      currentHp: 9,
      currentResilience: 5,
      strain: 0,
      items: [],
      appearance: "",
      relationships: [],
      notes: "",
    };
    addCharacter(newChar);
    setActiveCharacter(newChar.id);
  }, [addCharacter, setActiveCharacter]);

  const moveCharacterToTable = useCallback(() => {
    const activeChar = getActiveCharacter();
    if (!activeChar) return;

    openDialog({
      type: 'select',
      title: 'Move Character to Table',
      subtitle: activeChar.name,
      message: 'Select a table to move this character to:',
      options: tables.map((table) => ({
        id: table.id,
        label: table.name,
        disabled: table.id === activeTableId,
      })),
      confirmLabel: 'Move',
      onConfirm: (tableId) => {
        assignCharacterToTable(activeChar.id, tableId);
        // Switch to the destination table
        setActiveTable(tableId);
      },
    });
  }, [getActiveCharacter, openDialog, tables, activeTableId, assignCharacterToTable, setActiveTable]);

  const deleteCurrentTable = useCallback(() => {
    const table = getActiveTable();
    if (!table) return;

    if (table.id === 'Uncategorized' || table.id === '__uncategorized__') {
      // Can't delete the uncategorized table
      return;
    }

    openDialog({
      type: 'confirm',
      title: 'Delete Table',
      subtitle: table.name,
      message: `Delete this table? Characters in this table will be moved to Uncategorized.`,
      confirmLabel: 'Delete',
      theme: 'negative',
      onConfirm: () => {
        removeTable(table.id);
      },
    });
  }, [getActiveTable, openDialog, removeTable]);

  const renameCurrentTable = useCallback(() => {
    const table = getActiveTable();
    if (!table) return;

    openDialog({
      type: 'text-input',
      title: 'Rename Table',
      subtitle: table.name,
      inputLabel: 'New Name',
      initialValue: table.name,
      confirmLabel: 'Rename',
      onConfirm: (newName) => {
        if (newName.trim()) {
          renameTable(table.id, newName.trim());
        }
      },
    });
  }, [getActiveTable, openDialog, renameTable]);

  const importCharacterFromTable = useCallback(() => {
    // Get all characters from other tables
    const otherCharacters = allCharacters.filter((c: CharacterData) => c.belongsToTable !== activeTableId);

    if (otherCharacters.length === 0) {
      openDialog({
        type: 'confirm',
        title: 'No Characters Available',
        message: 'There are no characters in other tables to import.',
        confirmLabel: 'OK',
        onConfirm: () => {},
      });
      return;
    }

    // Build options with table context
    const options = otherCharacters.map((char: CharacterData) => {
      const charTable = tables.find((t) => t.id === char.belongsToTable);
      return {
        id: char.id,
        label: `${char.name} (from ${charTable?.name || 'Unknown'})`,
      };
    });

    openDialog({
      type: 'select',
      title: 'Import Character',
      subtitle: getActiveTable()?.name,
      message: 'Select a character to copy to this table:',
      options,
      confirmLabel: 'Import',
      onConfirm: (characterId) => {
        const sourceChar = allCharacters.find((c: CharacterData) => c.id === characterId);
        if (sourceChar) {
          // Create a copy of the character with a new ID and assign to current table
          const newCharacter = {
            ...sourceChar,
            id: generateUUID(),
            belongsToTable: activeTableId,
          };
          addCharacter(newCharacter);
          setActiveCharacter(newCharacter.id);
        }
      },
    });
  }, [allCharacters, activeTableId, tables, openDialog, getActiveTable, addCharacter, setActiveCharacter]);

  const handleCommand = useCallback((command: AppCommand) => {
    switch (command.type) {
      case "open-character-search":
        setIsCharacterSearchOpen(true);
        break;
      case "open-table-search":
        setIsTableSearchOpen(true);
        break;
      case "import-character":
        fileInputRef.current?.click();
        break;
      case "move-character-to-table":
        moveCharacterToTable();
        break;
      case "delete-table":
        deleteCurrentTable();
        break;
      case "rename-table":
        renameCurrentTable();
        break;
      case "import-character-from-table":
        importCharacterFromTable();
        break;
      case "heal-all":
        allCharacters.forEach((char) => {
          const hpMax = computeHpMax(char.coreStats);
          updateCharacter(char.id, { currentHp: hpMax });
        });
        break;
    }
  }, [allCharacters, updateCharacter, moveCharacterToTable, deleteCurrentTable, renameCurrentTable, importCharacterFromTable]);

  const actions = useMemo<ActionDefinition[]>(() => [
    {
      id: "import-character",
      label: "Import Character",
      description: "Add a character to the GM view",
      keywords: ["import", "add", "character"],
      category: "GM Tools",
      shortcut: "Meta+O"
    },
    {
      id: "open-character-search",
      label: "Switch Character",
      description: "Open character switcher",
      keywords: ["switch", "character", "change", "select"],
      shortcut: "C",
      category: "navigation",
    },
    // Table actions (shared with CharacterSheet)
    ...getTableActions(),
    {
      id: "heal-all",
      label: "Heal All Characters",
      description: "Restore all characters to full HP",
      keywords: ["heal", "restore", "all", "party"],
      category: "GM Tools"
    }
  ], []);

  const { Scope } = useFocus(
    "GmView",
    {},
    handleCommand,
    actions
  );

  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Split characters into active and minimized (filtered by current table)
  const activeCharacters = tableCharacters.filter((c) => !c.isMinimized);
  const minimizedCharacters = tableCharacters.filter((c) => c.isMinimized);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get character for roll search
  const rollSearchCharacter = rollSearchCharacterId
    ? allCharacters.find((c) => c.id === rollSearchCharacterId)
    : null;

  // Compute skills for roll search
  const rollSearchSkills: Skill[] = rollSearchCharacter
    ? computeSkills(rollSearchCharacter.coreStats)
    : [];

  const rollSearchCoreStats = rollSearchCharacter
    ? rollSearchCharacter.coreStats
    : { Mind: 0, Body: 0, Reflex: 0 };

  const rollSearchMovement = rollSearchCharacter
    ? computeMovement(rollSearchCharacter.coreStats)
    : 0;

  // Open character in new window
  const openCharacterInNewWindow = (characterId: string) => {
    window.open(
      `/character?id=${encodeURIComponent(characterId)}`,
      "_blank",
      "width=1200,height=900,menubar=no,toolbar=no,location=yes,status=no"
    );
  };

  // Handle roll for a character
  const handleRoll = useCallback(
    (characterId: string, skillName: string, poolSize: number, flatHits = 0) => {
      const character = allCharacters.find((c) => c.id === characterId);
      rollDice(skillName, poolSize, flatHits, character?.name);
    },
    [allCharacters, rollDice]
  );

  // Handle import
  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await importCharacterToCollection(file);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error importing character:", err);
      alert("Error importing character data.");
    }

    e.target.value = "";
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;

      // Ignore if typing in an input
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        return;
      }

      // Ignore if modifier keys (except for card actions)
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      const key = event.key.toLowerCase();

      // C: Open character search
      if (key === "c") {
        event.preventDefault();
        setIsCharacterSearchOpen(true);
        return;
      }

      // Left/Right arrows: Navigate between cards
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        const newIndex = Math.max(0, focusedIndex - 1);
        setFocusedIndex(newIndex);
        cardRefs.current[newIndex]?.focus();
        return;
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        const newIndex = Math.min(activeCharacters.length - 1, focusedIndex + 1);
        setFocusedIndex(newIndex);
        cardRefs.current[newIndex]?.focus();
        return;
      }

      // Up/Down arrows: Adjust value of currently focused stat
      if ((event.key === "ArrowUp" || event.key === "ArrowDown") && activeCharacters[focusedIndex]) {
        const statType = getFocusedStatType();
        if (statType) {
          event.preventDefault();
          const character = activeCharacters[focusedIndex];
          const isIncrement = event.key === "ArrowUp";

          if (statType === "hp") {
            const body = character.coreStats["Body"] ?? 0;
            const hpMax = body * 2 + 3;
            const newHp = isIncrement
              ? Math.min(hpMax, character.currentHp + 1)
              : Math.max(0, character.currentHp - 1);
            updateCharacter(character.id, { currentHp: newHp });
          } else if (statType === "resilience") {
            const resMax = computeResilienceMax(character.coreStats);
            const newRes = isIncrement
              ? Math.min(resMax, character.currentResilience + 1)
              : Math.max(0, character.currentResilience - 1);
            updateCharacter(character.id, { currentResilience: newRes });
          } else if (statType === "strain") {
            const newStrain = isIncrement
              ? character.strain + 1
              : Math.max(0, character.strain - 1);
            updateCharacter(character.id, { strain: newStrain });
          }
          return;
        }
      }

      // Number keys: Jump to card position (active cards only)
      if (/^[1-9]$/.test(event.key)) {
        event.preventDefault();
        const targetIndex = parseInt(event.key, 10) - 1;
        if (targetIndex < activeCharacters.length) {
          setFocusedIndex(targetIndex);
          cardRefs.current[targetIndex]?.focus();
        }
        return;
      }

      // Character-specific actions (R, D, H, X, V, S) are now handled by CompactCharacterCard via focus system

      // Tab / Shift+Tab: Native behavior (no override)
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [focusedIndex, activeCharacters, updateCharacter]);

  // Dialog helper functions for character-specific actions
  const openDamageDialog = useCallback((characterId: string) => {
    const character = allCharacters.find((c) => c.id === characterId);
    if (!character) return;

    openDialog({
      type: 'input',
      ...DAMAGE_DIALOG,
      subtitle: character.name,
      onConfirm: (amount) => {
        // Damage goes to resilience first, then HP
        let remainingDamage = amount;
        let newResilience = character.currentResilience;
        let newHp = character.currentHp;

        if (newResilience > 0) {
          const resilienceDamage = Math.min(newResilience, remainingDamage);
          newResilience -= resilienceDamage;
          remainingDamage -= resilienceDamage;
        }
        if (remainingDamage > 0) {
          newHp = Math.max(0, newHp - remainingDamage);
        }

        updateCharacter(characterId, {
          currentResilience: newResilience,
          currentHp: newHp,
        });
      },
    });
  }, [allCharacters, openDialog, updateCharacter]);

  const openHealDialog = useCallback((characterId: string) => {
    const character = allCharacters.find((c) => c.id === characterId);
    if (!character) return;

    const hpMax = computeHpMax(character.coreStats);

    openDialog({
      type: 'input',
      ...HEAL_DIALOG,
      subtitle: character.name,
      onConfirm: (amount) => {
        updateCharacter(characterId, {
          currentHp: Math.min(hpMax, character.currentHp + amount),
        });
      },
    });
  }, [allCharacters, openDialog, updateCharacter]);

  const openDirectDamageDialog = useCallback((characterId: string) => {
    const character = allCharacters.find((c) => c.id === characterId);
    if (!character) return;

    openDialog({
      type: 'input',
      ...DIRECT_DAMAGE_DIALOG,
      subtitle: character.name,
      onConfirm: (amount) => {
        updateCharacter(characterId, {
          currentHp: Math.max(0, character.currentHp - amount),
        });
      },
    });
  }, [allCharacters, openDialog, updateCharacter]);

  const openSpendResilienceDialog = useCallback((characterId: string) => {
    const character = allCharacters.find((c) => c.id === characterId);
    if (!character) return;

    openDialog({
      type: 'input',
      ...SPEND_RESILIENCE_DIALOG,
      subtitle: character.name,
      onConfirm: (amount) => {
        updateCharacter(characterId, {
          currentResilience: Math.max(0, character.currentResilience - amount),
        });
      },
    });
  }, [allCharacters, openDialog, updateCharacter]);

  const openDeleteDialog = useCallback((characterId: string) => {
    const character = allCharacters.find((c) => c.id === characterId);
    if (!character) return;

    openDialog({
      type: 'confirm',
      ...DELETE_CHARACTER_DIALOG,
      subtitle: character.name,
      message: DELETE_CHARACTER_DIALOG.getMessage(character.name),
      onConfirm: () => {
        removeCharacter(characterId);
      },
    });
  }, [allCharacters, openDialog, removeCharacter]);

  // Handle tag action with notification
  const handleTagAction = useCallback(
    (characterId: string, action: TagAction) => {
      const character = allCharacters.find((c) => c.id === characterId);
      if (!character) return;

      const hpMax = computeHpMax(character.coreStats);
      const { type, value = 0, sourceName, sourceType } = action;

      // Get dialog configuration from centralized messages
      const { sourceLabel, ...dialogConfig } = getTagActionDialog(type, value, sourceName, sourceType);

      // Open confirmation dialog
      openDialog({
        type: 'confirm',
        ...dialogConfig,
        subtitle: `${character.name} — ${sourceLabel}`,
        onConfirm: () => {
          // Add notification via context
          addTagNotification(action);

          // Apply effect
          switch (type) {
            case TagActionType.GAIN_HEALTH:
              updateCharacter(characterId, {
                currentHp: Math.min(hpMax, character.currentHp + value),
              });
              break;
            case TagActionType.LOSE_HEALTH:
              updateCharacter(characterId, {
                currentHp: Math.max(0, character.currentHp - value),
              });
              break;
            case TagActionType.LOSE_RESILIENCE:
              updateCharacter(characterId, {
                currentResilience: Math.max(0, character.currentResilience - value),
              });
              break;
            case TagActionType.ACQUIRE_STRAIN:
              updateCharacter(characterId, {
                strain: character.strain + value,
              });
              break;
          }
        },
      });
    },
    [allCharacters, updateCharacter, addTagNotification, openDialog]
  );

  // Update handlers for cards
  const createCardHandlers = (character: CharacterData) => ({
    onUpdateHp: (hp: number) => updateCharacter(character.id, { currentHp: hp }),
    onUpdateResilience: (res: number) =>
      updateCharacter(character.id, { currentResilience: res }),
    onUpdateStrain: (strain: number) =>
      updateCharacter(character.id, { strain }),
    onApplyStrainDamage: (damage: number) => {
      updateCharacter(character.id, {
        currentHp: Math.max(0, character.currentHp - damage),
      });
      // Create notification for strain damage (strain does NOT clear after combat per rules)
      addTagNotification({
        type: TagActionType.LOSE_HEALTH,
        value: damage,
        sourceName: `${character.name}: End of Combat Strain`,
      });
    },
    onOpenRoll: () => {
      setRollSearchCharacterId(character.id);
      setIsRollSearchOpen(true);
    },
    onOpenDamageDialog: () => {
      openDamageDialog(character.id);
    },
    onOpenHealDialog: () => {
      openHealDialog(character.id);
    },
    onOpenDirectDamageDialog: () => {
      openDirectDamageDialog(character.id);
    },
    onOpenSpendResilienceDialog: () => {
      openSpendResilienceDialog(character.id);
    },
    onMinimize: () => {
      updateCharacter(character.id, { isMinimized: true });
      // If this was the active character, switch to another active character
      if (character.id === activeCharacterId) {
        const otherActive = activeCharacters.find((c) => c.id !== character.id);
        if (otherActive) {
          setActiveCharacter(otherActive.id);
        }
      }
    },
    onOpenInNewTab: () => openCharacterInNewWindow(character.id),
    onUpdateItem: (itemId: string, content: string) => {
      const updatedItems = character.items.map((item) =>
        item.id === itemId ? { ...item, content } : item
      );
      updateCharacter(character.id, { items: updatedItems });
    },
    onDeleteItem: (itemId: string) => {
      const updatedItems = character.items.filter((item) => item.id !== itemId);
      updateCharacter(character.id, { items: updatedItems });
    },
    onTagAction: (action: TagAction) => {
      handleTagAction(character.id, action);
    },
    onRoll: (name: string, pool: number, flatHits?: number) => {
      handleRoll(character.id, name, pool, flatHits);
    },
  });

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>GM View</h1>
        <div className={styles.headerActions}>
          <div
            onClick={() => setIsTableSearchOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsTableSearchOpen(true);
              }
            }}
            role="button"
            tabIndex={0}
            style={{
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}
            title={`Current table: ${activeTable?.name || 'Unknown'} (click to switch)`}
          >
            <Badge variant="navigation">{activeTable?.name || 'Unknown'}</Badge>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".json"
            onChange={handleImport}
          />
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            variant="secondary"
            size="sm"
          >
            Import Character
          </Button>
          <Button
            type="button"
            onClick={() => setIsCharacterSearchOpen(true)}
            variant="primary"
            size="sm"
            title="Hotkey: C"
          >
            Switch Character
          </Button>
          <button
            type="button"
            className={styles.settingsButton}
            onClick={() => setIsSettingsOpen(true)}
            title="Settings"
            aria-label="Open settings"
          >
            <span aria-hidden="true">&#x2699;</span>
          </button>
        </div>
      </header>

      <div className={styles.helpText}>
        <strong>C</strong> switch &bull; <strong>←/→</strong> cards &bull;{" "}
        <strong>Tab</strong> cycle &bull; <strong>↑/↓</strong> adjust &bull;{" "}
        <strong>1-9</strong> jump &bull; <strong>R</strong> roll &bull;{" "}
        <strong>D</strong> damage &bull; <strong>H</strong> heal &bull;{" "}
        <strong>X</strong> direct &bull; <strong>V</strong> res &bull;{" "}
        <strong>S</strong> strain &bull; <strong>/</strong> commands
      </div>

      <Scope activateOn="mousedown">
        {tableCharacters.length === 0 ? (
          <EmptyTableState
            tableName={activeTable?.name || 'this table'}
            onCreateCharacter={createNewCharacter}
          />
        ) : (
          <div className={styles.cardGrid}>
            {activeCharacters.map((character, index) => (
              <CompactCharacterCard
                key={character.id}
                ref={(el) => {
                  cardRefs.current[index] = el;
                }}
                character={character}
                isFocused={index === focusedIndex}
                onFocusCard={() => setFocusedIndex(index)}
                {...createCardHandlers(character)}
              />
            ))}
          </div>
        )}
      </Scope>

      {minimizedCharacters.length > 0 && (
        <div className={styles.minimizedSection}>
          <button
            type="button"
            className={styles.minimizedHeader}
            onClick={() => setIsMinimizedExpanded(!isMinimizedExpanded)}
          >
            <span className={styles.minimizedToggle}>
              {isMinimizedExpanded ? "▼" : "▶"}
            </span>
            Minimized ({minimizedCharacters.length})
          </button>
          {isMinimizedExpanded && (
            <div className={styles.minimizedGrid}>
              {minimizedCharacters.map((character) => (
                <MinimizedCharacterCard
                  key={character.id}
                  character={character}
                  onRestore={() => updateCharacter(character.id, { isMinimized: false })}
                  onDelete={() => openDeleteDialog(character.id)}
                  onOpen={() => openCharacterInNewWindow(character.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Character Search Modal */}
      <CharacterSearch
        isOpen={isCharacterSearchOpen}
        onClose={() => setIsCharacterSearchOpen(false)}
        characters={tableCharacters}
        activeCharacterId={activeCharacterId}
        onSelect={(id) => {
          const selectedCharacter = tableCharacters.find((c) => c.id === id);
          // Restore if minimized
          if (selectedCharacter?.isMinimized) {
            updateCharacter(id, { isMinimized: false });
            // Restored character will appear at end of active list
            setFocusedIndex(activeCharacters.length);
          } else {
            // Find index in current active characters
            const index = activeCharacters.findIndex((c) => c.id === id);
            if (index >= 0) {
              setFocusedIndex(index);
              cardRefs.current[index]?.focus();
            }
          }
          setActiveCharacter(id);
        }}
      />

      {/* Table Search Modal */}
      <TableSearch
        isOpen={isTableSearchOpen}
        onClose={() => setIsTableSearchOpen(false)}
        tables={tables}
        activeTableId={activeTableId}
        onSelect={(id) => {
          setActiveTable(id);
          setIsTableSearchOpen(false);
        }}
        onCreateNew={(name) => {
          addTable(name);
        }}
      />

      {/* Roll Search Modal */}
      {rollSearchCharacter && (
        <RollSearch
          isOpen={isRollSearchOpen}
          onClose={() => {
            setIsRollSearchOpen(false);
            setRollSearchCharacterId(null);
          }}
          skills={rollSearchSkills}
          coreStats={rollSearchCoreStats}
          movement={rollSearchMovement}
          characterName={rollSearchCharacter?.name}
          onRoll={(name, pool, flatHits) => {
            if (rollSearchCharacterId) {
              handleRoll(rollSearchCharacterId, name, pool, flatHits);
            }
          }}
        />
      )}

      {/* Action Dialog */}


      {/* Settings Popup */}
      <SettingsPopup
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
    </div>
  );
};

export default GmView;
